/**
 * ModalFokusTrap — sentral klasse som modaler kaller når de åpnes/lukkes.
 * Når en modal er åpen:
 * - HiddenActions-knappene får `tabindex="-1"` slik at Tab ikke hopper
 *   ut av modal-konteksten.
 * - AriaMirror får `aria-modal="true"` så skjermlesere kan signalisere
 *   modus-skiftet.
 *
 * Strategi:
 * - Stack-basert: hvis flere modaler åpnes oppå hverandre (sjelden,
 *   men kan skje med confirm-dialoger over hovedmodaler), holder vi
 *   en teller. Først når den når 0 gjenopprettes fokus.
 * - Trygt å bruke uten å ha mount'et HiddenActions først — funksjoner
 *   sjekker for `hiddenActions.rot` indirekte via no-op.
 *
 * Bruk i modaler:
 *   modalFokusTrap.aapne('Innboks');
 *   ...
 *   modalFokusTrap.lukk();
 *
 * Bygd i runde 42.
 */
import { hiddenActions } from '@/ui/HiddenActions';

const ARIA_HUD_ID = 'forlagshuset-aria-hud';

export class ModalFokusTrap {
  private aapneTeller = 0;
  private stack: string[] = [];

  /**
   * Markerer at en modal har åpnet seg. Tittel brukes til
   * loggføring / debugging. Det er trygt å kalle uten en motpart-
   * `lukk()` — men da blir HiddenActions værende med tabindex=-1
   * inntil noen kaller `lukk()` eller `nullstill()`.
   */
  aapne(tittel: string): void {
    this.stack.push(tittel);
    this.aapneTeller += 1;
    if (this.aapneTeller === 1) {
      hiddenActions.settTabbable(false);
      this.settAriaModal(true);
    }
  }

  /**
   * Markerer at en modal er lukket. Når alle modaler er lukket
   * gjenopprettes tabindex og aria-modal fjernes.
   */
  lukk(): void {
    if (this.aapneTeller === 0) return;
    this.stack.pop();
    this.aapneTeller -= 1;
    if (this.aapneTeller === 0) {
      hiddenActions.settTabbable(true);
      this.settAriaModal(false);
    }
  }

  /**
   * Force-reset. Brukes i tester.
   */
  nullstill(): void {
    this.aapneTeller = 0;
    this.stack = [];
    hiddenActions.settTabbable(true);
    this.settAriaModal(false);
  }

  /** Antall åpne modaler. Brukes av tester. */
  get aapneAntall(): number {
    return this.aapneTeller;
  }

  /** Toppen av modal-stacken. Brukes av tester / debug. */
  get aktivTittel(): string | undefined {
    return this.stack[this.stack.length - 1];
  }

  private settAriaModal(aktiv: boolean): void {
    if (typeof document === 'undefined') return;
    const hud = document.getElementById(ARIA_HUD_ID);
    if (!hud) return;
    if (aktiv) hud.setAttribute('aria-modal', 'true');
    else hud.removeAttribute('aria-modal');
  }
}

export const modalFokusTrap = new ModalFokusTrap();
